import { createContext, useContext, useEffect, useMemo, useState } from "react";
import * as React from "react";
import axios from "axios";
import { Branch, useLocationConfiguration } from "./LocationConfiguration";
// import { ApplicationData } from "@tlc/ls2pac/app/ApplicationData";

interface BranchLimitContextType {
  branchLimits: BranchLimit[];
  selectedBranchIds: readonly BranchLimit["identifier"][];
  selectedBranches: BranchLimit[];
  isConfigLimited: boolean;
  displayBranchLimitControl: boolean;
  displayInCopiesBranchIds: readonly BranchLimit["identifier"][];
  visibleBranches: BranchLimit[];
  /**
   * selected branches or config limited branches
   */
  limitedBranchIds: readonly BranchLimit["identifier"][];
  configLimitedBranches: Branch[];
  updateSelectedBranches: (
    selectedBranchIds: BranchLimit["identifier"][]
  ) => void;
}

// Create the Branch Limit Context
const BranchLimitContext = createContext<BranchLimitContextType>(null);

export const useBranchLimits = () => {
  const context = useContext(BranchLimitContext);

  if (!context) {
    throw new Error(
      "useBranchLimits must be used within a BranchLimitProvider"
    );
  }

  return context;
};

// This replaces branchLimitsUtil in ApplicationData
// eventually when the whole app is in React this can merge with BranchLimitProvider
export const BranchLimitsHolder = (() => {
  let isConfigLimited: Readonly<boolean> | null = null;
  let branchLimits: Readonly<BranchLimit>[] | null = null;
  let pendingGetBranchLimiting: Promise<BranchLimiting> = null;
  // TODO validate that branches selected are not hidden and are in branchLimits
  let selectedBranchIds: BranchLimit["identifier"][] = [];
  const subscribers: ((event: {
    selectedBranchIds: Readonly<BranchLimit["identifier"][]>;
  }) => void)[] = []; // Array to hold subscriber functions

  const getSelectedBranchIds = (): Readonly<BranchLimit["identifier"][]> => {
    return [...selectedBranchIds];
  };

  const setSelectedBranchIds = (
    limitedBranches: BranchLimit["identifier"][]
  ) => {
    selectedBranchIds = [...limitedBranches];
    subscribers.forEach((subscriber) =>
      subscriber({
        selectedBranchIds,
      })
    );
  };

  const subscribe = (
    subscriber: (event: {
      selectedBranchIds: Readonly<BranchLimit["identifier"][]>;
    }) => void
  ) => {
    // Add the subscriber function to the list of subscribers
    subscribers.push(subscriber);

    subscriber({ selectedBranchIds: [...selectedBranchIds] });

    // Return an unsubscribe function
    return () => {
      const index = subscribers.indexOf(subscriber);
      if (index !== -1) {
        subscribers.splice(index, 1); // Remove the subscriber when unsubscribed
      }
    };
  };

  /**
   * static data once fetched
   */
  const fetchBranchLimits = async () => {
    if (branchLimits?.length) {
      return {
        isConfigLimited,
        branchLimits,
      };
    }

    if (pendingGetBranchLimiting) {
      return pendingGetBranchLimiting;
    }

    pendingGetBranchLimiting = axios
      .get<BranchLimiting>("/branch/branchLimits")
      .then(({ data }) => {
        isConfigLimited = data.isConfigLimited;
        branchLimits = data.branchLimits;
        return {
          isConfigLimited,
          branchLimits,
        };
      });

    return pendingGetBranchLimiting;
  };

  return {
    setSelectedBranchIds,
    getSelectedBranchIds,
    subscribe,
    fetchBranchLimits,
  };
})();

export interface BranchLimit {
  displayName: string;
  longDescription: string;
  identifier: string;
  /**
   * @deprecated
   */
  selected: boolean;
  hidden: boolean;
  displayInCopies: boolean;
  displayInBranchLimits: boolean;
  displayInPickupLocation: boolean;
}

export interface BranchLimiting {
  isConfigLimited: boolean;
  branchLimits: BranchLimit[];
}

// Branch Limit Context Provider Component
export const BranchLimitProvider = ({ children }: React.PropsWithChildren) => {
  const [branchLimits, setBranchLimits] = useState<BranchLimit[]>(null);
  const [isConfigLimited, setIsConfigLimited] = useState<boolean>(null);
  const {
    locationConfiguration: { filterBranches } = { filterBranches: null },
  } = useLocationConfiguration();
  const [selectedBranchIds, setSelectedBranchIds] = useState<
    Readonly<BranchLimit["identifier"][]>
  >(BranchLimitsHolder.getSelectedBranchIds());
  const selectedBranches = useMemo(() => {
    if (!branchLimits?.length) {
      return [];
    }

    return branchLimits.filter((b) => selectedBranchIds.includes(b.identifier));
  }, [branchLimits, selectedBranchIds]);

  const limitedBranchIds = useMemo(() => {
    if (selectedBranchIds?.length) {
      return selectedBranchIds;
    }

    return (filterBranches || [])
      .filter((branch) => branch.shared == false)
      .map((branch) => branch.identifier);
  }, [isConfigLimited, filterBranches, selectedBranchIds]);

  const visibleBranches = React.useMemo(
    () =>
      (branchLimits || []).filter(
        (branch) => branch.hidden === false && branch.displayInCopies
      ),
    [branchLimits]
  );

  const displayInCopiesBranchIds = React.useMemo(
    () =>
      (branchLimits || [])
        .filter((branch) => branch.displayInCopies)
        .map(({ identifier }) => identifier),
    [branchLimits]
  );

  const configLimitedBranches = React.useMemo(
    () => (filterBranches || []).filter((branch) => (branch.shared = false)),
    [filterBranches]
  );
  const displayBranchLimitControl = React.useMemo(
    () => visibleBranches.length > 1 && configLimitedBranches.length !== 1,
    [visibleBranches, configLimitedBranches]
  );

  const updateSelectedBranches = (
    limitedBranches: BranchLimit["identifier"][]
  ) => {
    //   const updatedBranchLimits = branchLimits.map(branch => ({
    //     ...branch,
    //     selected: limitedBranches.includes(branch.identifier)
    // }))
    //   setBranchLimits(updatedBranchLimits);
    BranchLimitsHolder.setSelectedBranchIds(limitedBranches);
  };

  // Fetch branch limits on load
  useEffect(() => {
    BranchLimitsHolder.fetchBranchLimits().then(
      ({ branchLimits, isConfigLimited }) => {
        setBranchLimits(branchLimits);

        setIsConfigLimited(isConfigLimited);
      }
    );
    const unsubscribe = BranchLimitsHolder.subscribe(
      ({ selectedBranchIds: selectedIds }) => setSelectedBranchIds(selectedIds)
    );
    return () => {
      // cleanup
      unsubscribe();
    };
  }, []);

  // Listen for Logout and clear current limits
  useEffect(() => {
    const handleLogout = () => {
      // Clear current limits
      // Example: setBranchLimits(null);
    };

    // Subscribe to logout event
    // Example: subscribeToEvent('logout', handleLogout);

    // Unsubscribe on component unmount
    return () => {
      // Unsubscribe from logout event
      // Example: unsubscribeFromEvent('logout', handleLogout);
    };
  }, []);

  // Listen for search and update branch filters if necessary
  useEffect(() => {
    // const handleSearch = (searchParams) => {
    // Update branch filters if necessary from search params
    // Example: updateBranchFilters(searchParams);
    // };

    // Subscribe to search event
    // Example: subscribeToEvent('search', handleSearch);

    // Unsubscribe on component unmount
    return () => {
      // Unsubscribe from search event
      // Example: unsubscribeFromEvent('search', handleSearch);
    };
  }, []);

  // Define the context value
  // TODO memoize members and context object
  // potentially split less stable data like selectedBranches from the rest
  const contextValue = {
    branchLimits,
    selectedBranchIds,
    selectedBranches,
    visibleBranches,
    displayInCopiesBranchIds,
    configLimitedBranches,
    limitedBranchIds,
    isConfigLimited,
    updateSelectedBranches,
    displayBranchLimitControl,
  };

  return (
    <BranchLimitContext.Provider value={contextValue}>
      {children}
    </BranchLimitContext.Provider>
  );
};
